import type { AdType } from '../../types/campaign';
import { AdTypeBadge, AdTypeDropdown } from './AdTypeDropdown';
import type { DriveFileSummary } from './types';

// One asset card. Pure presentational — the grid owns label state and the
// onLabelChange / onPreview callbacks.

type Props = {
  file: DriveFileSummary;
  label: string;
  onLabelChange: (next: string) => void;
  onPreview: () => void;
  // Ad Preview Mockups Stage 1 §2.1. adType lives alongside the label; the
  // card surfaces a dropdown in edit mode and a badge in view mode. Both
  // adType and onAdTypeChange are optional so callers that don't yet need
  // the typing UI (legacy Local Assets browser surfaces during rollout)
  // can opt in incrementally without prop-explosion.
  adType?: AdType;
  onAdTypeChange?: (next: AdType) => void;
  // When true, the card renders with an "orphan" warning: a Campaign label
  // points at a file id that no longer appears in the live folder listing.
  // The card still surfaces the stored label so the AM can decide whether
  // to re-add the file in Drive or remove the orphan label entry.
  orphan?: boolean;
  // P2c polish round 1 (Fix 2): view-only mode. When true, the label
  // input is replaced with read-only display text. Lightbox / preview
  // wiring still works; only the input is suppressed. Editing happens
  // in the Campaign editor (mode='edit').
  readOnly?: boolean;
};

const IMAGE_MIME_PREFIX = 'image/';
const VIDEO_MIME_PREFIX = 'video/';

function fileTypeBadge(mimeType: string): string {
  if (mimeType.startsWith(IMAGE_MIME_PREFIX)) return 'Image';
  if (mimeType.startsWith(VIDEO_MIME_PREFIX)) return 'Video';
  // Strip "application/" + tail for a cleaner label e.g. "application/pdf" → "PDF".
  const tail = mimeType.split('/').pop() ?? mimeType;
  return tail.toUpperCase();
}

function formatSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DriveAssetCard({
  file,
  label,
  onLabelChange,
  onPreview,
  adType,
  onAdTypeChange,
  orphan,
  readOnly,
}: Props) {
  const isImage = file.mimeType.startsWith(IMAGE_MIME_PREFIX);
  const isVideo = file.mimeType.startsWith(VIDEO_MIME_PREFIX);
  const previewable = isImage || isVideo;
  const size = formatSize(file.size);

  // adType UI is wired only when the parent opted in (onAdTypeChange
  // defined for edit, or a non-null badge value for view). Older callers
  // that omit both props get the legacy card unchanged.
  const showAdTypeDropdown = !readOnly && onAdTypeChange !== undefined;
  const showAdTypeBadge = readOnly && adType !== undefined && adType !== null;

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-md border bg-white ${
        orphan ? 'border-amber-300' : 'border-slate-200'
      }`}
    >
      <button
        type="button"
        onClick={onPreview}
        aria-label={`Preview ${file.name}`}
        className="flex h-32 items-center justify-center bg-slate-100 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
      >
        {file.thumbnailUrl ? (
          <img
            src={file.thumbnailUrl}
            alt=""
            className="max-h-full max-w-full object-contain"
            loading="lazy"
          />
        ) : file.iconUrl ? (
          <img src={file.iconUrl} alt="" className="h-12 w-12" />
        ) : (
          <span className="text-xs text-slate-400">No preview</span>
        )}
      </button>

      <div className="flex flex-1 flex-col space-y-2 p-3">
        <div className="min-w-0">
          <p
            className="truncate text-sm font-medium text-slate-900"
            title={file.name}
          >
            {file.name}
          </p>
          <p className="text-xs text-slate-500">
            {fileTypeBadge(file.mimeType)}
            {size ? ` · ${size}` : ''}
            {!previewable && file.webViewLink ? ' · view in Drive' : ''}
          </p>
        </div>

        {orphan && (
          <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
            File no longer in this folder. Re-add in Drive or clear the label.
          </p>
        )}

        {readOnly ? (
          <div className="block text-xs font-medium text-slate-700">
            Label
            <p className="mt-1 break-words text-sm font-normal text-slate-900">
              {label || <span className="italic text-slate-400">(none)</span>}
            </p>
            {showAdTypeBadge && (
              <div className="mt-1">
                <AdTypeBadge adType={adType} />
              </div>
            )}
          </div>
        ) : (
          <>
            <label className="block text-xs font-medium text-slate-700">
              Label
              <input
                type="text"
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={label}
                onChange={(e) => onLabelChange(e.target.value)}
                placeholder="Enter label for this asset…"
              />
            </label>
            {showAdTypeDropdown && (
              <label className="block text-xs font-medium text-slate-700">
                Ad type
                <AdTypeDropdown
                  value={adType}
                  onChange={(next) => onAdTypeChange!(next)}
                  ariaLabel={`Ad type for ${file.name}`}
                />
              </label>
            )}
          </>
        )}
      </div>
    </div>
  );
}
